import { useState, useRef, useEffect } from 'react'
import { ChevronRight, ChevronDown, Trash2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useStudioLocale } from '@/i18n'
import { ConfirmDelete } from '../confirm-delete'
import type { SchemaField } from './types'
import { SCHEMA_FIELD_TYPES } from './types'
import { useFieldSuggestions } from './use-field-suggestions'
import type { EntityIndex } from '../../hooks/use-entity-index'

interface SchemaFieldRowProps {
  field: SchemaField
  depth: number
  onUpdate: (field: SchemaField) => void
  onDelete: () => void
  onRename?: (oldName: string, newName: string) => void
  pathPrefix?: string
  entityIndex?: EntityIndex
  suggestionSlot?: React.ReactNode
  autoFocus?: boolean
}

export function SchemaFieldRow({
  field,
  depth,
  onUpdate,
  onDelete,
  onRename,
  pathPrefix = '',
  entityIndex,
  suggestionSlot,
  autoFocus,
}: SchemaFieldRowProps) {
  const [expanded, setExpanded] = useState(true)
  const [localName, setLocalName] = useState(field.name)
  const [deleteConfirm, setDeleteConfirm] = useState(false)
  const [typeChangeConfirm, setTypeChangeConfirm] = useState<string | null>(null)
  const [showDropdown, setShowDropdown] = useState(false)
  const [highlightIdx, setHighlightIdx] = useState(0)
  const nameRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const suggestions = useFieldSuggestions(localName, entityIndex)
  const confirmCopy = useStudioLocale().strings.dataValidation.confirm

  // Debounced dropdown toggle
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowDropdown(suggestions !== null && suggestions.length > 0)
      setHighlightIdx(0)
    }, 150)
    return () => clearTimeout(timer)
  }, [suggestions])

  // Close on outside click
  useEffect(() => {
    if (!showDropdown) return
    const handler = (e: MouseEvent) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target as Node) &&
        nameRef.current && !nameRef.current.contains(e.target as Node)
      ) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [showDropdown])

  function selectSuggestion(name: string) {
    setLocalName(name)
    setShowDropdown(false)
    onUpdate({ ...field, name })
  }

  // Sync local name with prop
  useEffect(() => {
    setLocalName(field.name)
  }, [field.name])

  // Auto-focus on new field
  useEffect(() => {
    if (autoFocus && nameRef.current) {
      nameRef.current.focus()
    }
  }, [autoFocus])

  const hasChildren = field.type === 'object' && field.children
  const isExpandable = field.type === 'object' || field.type === 'array'

  function handleNameBlur() {
    if (localName !== field.name) {
      if (onRename && field.name) {
        const oldFullPath = pathPrefix ? `${pathPrefix}.${field.name}` : field.name
        const newFullPath = pathPrefix ? `${pathPrefix}.${localName}` : localName
        onRename(oldFullPath, newFullPath)
      }
      onUpdate({ ...field, name: localName })
    }
  }

  function handleNameKeyDown(e: React.KeyboardEvent) {
    if (showDropdown && suggestions) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setHighlightIdx((i) => Math.min(i + 1, suggestions.length - 1))
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setHighlightIdx((i) => Math.max(i - 1, 0))
        return
      }
      if (e.key === 'Enter') {
        e.preventDefault()
        selectSuggestion(suggestions[highlightIdx])
        return
      }
      if (e.key === 'Escape') {
        setShowDropdown(false)
        return
      }
    }
    if (e.key === 'Enter') {
      ;(e.target as HTMLInputElement).blur()
    }
  }

  function handleTypeChange(newType: string) {
    const typedType = newType as SchemaField['type']

    // If changing FROM object and has children, confirm
    if (field.type === 'object' && typedType !== 'object' && field.children && field.children.length > 0) {
      setTypeChangeConfirm(newType)
      return
    }

    applyTypeChange(typedType)
  }

  function applyTypeChange(newType: SchemaField['type']) {
    const updated: SchemaField = { ...field, type: newType }

    if (newType === 'object') {
      updated.children = updated.children ?? []
      delete updated.items
    } else if (newType === 'array') {
      updated.items = updated.items ?? { name: 'item', type: 'string' }
      delete updated.children
    } else {
      delete updated.children
      delete updated.items
    }

    onUpdate(updated)
    setTypeChangeConfirm(null)
  }

  function handleToggleRequired() {
    onUpdate({ ...field, required: !field.required })
  }

  if (deleteConfirm) {
    const childCount = field.children?.length ?? 0
    return (
      <div style={{ paddingLeft: depth * 24 }}>
        <ConfirmDelete
          variant="delete"
          entityName={field.name || confirmCopy.unnamedField}
          onConfirm={onDelete}
          onCancel={() => setDeleteConfirm(false)}
          warning={
            childCount > 0
              ? confirmCopy.nestedFieldWarning(childCount)
              : undefined
          }
        />
      </div>
    )
  }

  // A type change off `object` drops the field's children (see
  // `applyTypeChange`), so it is destructive and still confirms. But it is NOT
  // a delete: this used to render "Delete type change for "x"?" by stuffing an
  // English clause into the identifier slot, which under `ko` produced a
  // foreign fragment inside the Korean sentence AND called a type change a
  // delete. The `typeChange` variant is the truthful wording; the guard is
  // unchanged.
  if (typeChangeConfirm) {
    const childCount = field.children?.length ?? 0
    return (
      <div style={{ paddingLeft: depth * 24 }}>
        <ConfirmDelete
          variant="typeChange"
          entityName={field.name || confirmCopy.unnamedField}
          onConfirm={() => applyTypeChange(typeChangeConfirm as SchemaField['type'])}
          onCancel={() => setTypeChangeConfirm(null)}
          warning={confirmCopy.nestedFieldWarning(childCount)}
        />
      </div>
    )
  }

  return (
    <>
      <div
        className="group flex items-center h-8 hover:bg-muted/50 transition-colors"
        style={{ paddingLeft: depth * 24 }}
        data-testid={`schema-field-row-${field.name || 'new'}`}
      >
        {/* Expand/collapse chevron */}
        <button
          className={cn(
            'w-5 h-5 flex items-center justify-center shrink-0',
            isExpandable ? 'text-muted-foreground cursor-pointer' : 'invisible',
          )}
          onClick={() => isExpandable && setExpanded(!expanded)}
          aria-label={expanded ? 'Collapse' : 'Expand'}
          tabIndex={isExpandable ? 0 : -1}
        >
          {isExpandable && (expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />)}
        </button>

        {/* Field name input with auto-complete */}
        <div className="relative flex-1 min-w-[120px]">
          <input
            ref={nameRef}
            value={localName}
            onChange={(e) => setLocalName(e.target.value)}
            onBlur={handleNameBlur}
            onKeyDown={handleNameKeyDown}
            placeholder="field_name"
            className="w-full h-6 text-[13px] font-mono bg-transparent border-b border-transparent hover:border-border focus:border-ring focus:outline-none px-1"
            aria-label="Field name"
          />
          {showDropdown && suggestions && (
            <div
              ref={dropdownRef}
              className="absolute left-0 top-full z-50 mt-0.5 bg-popover border border-border rounded-md shadow-md max-h-40 overflow-y-auto"
              style={{ minWidth: nameRef.current?.offsetWidth }}
            >
              {suggestions.slice(0, 8).map((s, i) => (
                <div
                  key={s}
                  className={cn(
                    'px-2 py-1 text-[13px] font-mono cursor-pointer',
                    i === highlightIdx ? 'bg-accent text-accent-foreground' : 'hover:bg-accent',
                  )}
                  onMouseDown={() => selectSuggestion(s)}
                >
                  {s}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Type select */}
        <select
          value={field.type}
          onChange={(e) => handleTypeChange(e.target.value)}
          className="w-20 h-6 text-xs text-muted-foreground bg-transparent border-none focus:outline-none focus:ring-1 focus:ring-ring rounded cursor-pointer"
          aria-label="Field type"
        >
          {SCHEMA_FIELD_TYPES.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>

        {/* Required toggle */}
        <button
          onClick={handleToggleRequired}
          className="w-5 h-5 flex items-center justify-center shrink-0 mx-1"
          title="Toggle required"
          aria-label="Toggle required"
        >
          <span
            className={cn(
              'inline-block w-1.5 h-1.5 rounded-full',
              field.required ? 'bg-primary' : 'border border-muted-foreground',
            )}
          />
        </button>

        {/* Delete button */}
        <button
          onClick={() => setDeleteConfirm(true)}
          className="w-5 h-5 flex items-center justify-center shrink-0 opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-all"
          aria-label="Delete field"
        >
          <Trash2 size={12} />
        </button>
      </div>

      {/* Suggestion slot */}
      {suggestionSlot}

      {/* Nested children for object */}
      {field.type === 'object' && expanded && (
        <div className="transition-all duration-150 overflow-hidden">
          {(field.children ?? []).map((child, i) => {
            const childPrefix = pathPrefix ? `${pathPrefix}.${field.name}` : field.name
            return (
              <SchemaFieldRow
                key={`${depth + 1}-${i}`}
                field={child}
                depth={depth + 1}
                onUpdate={(updated) => {
                  const newChildren = [...(field.children ?? [])]
                  newChildren[i] = updated
                  onUpdate({ ...field, children: newChildren })
                }}
                onDelete={() => {
                  const newChildren = (field.children ?? []).filter((_, idx) => idx !== i)
                  onUpdate({ ...field, children: newChildren })
                }}
                onRename={onRename}
                pathPrefix={childPrefix}
                entityIndex={entityIndex}
              />
            )
          })}
        </div>
      )}

      {/* Array items schema */}
      {field.type === 'array' && expanded && field.items && (
        <div className="transition-all duration-150 overflow-hidden">
          <SchemaFieldRow
            field={field.items}
            depth={depth + 1}
            onUpdate={(updated) => {
              onUpdate({ ...field, items: updated })
            }}
            onDelete={() => {
              onUpdate({ ...field, items: { name: 'item', type: 'string' } })
            }}
            onRename={onRename}
            pathPrefix={pathPrefix ? `${pathPrefix}.${field.name}` : field.name}
            entityIndex={entityIndex}
          />
        </div>
      )}
    </>
  )
}
